import apiClient from './apiClient';

/** R40: the cycle carries its role; the test-cycle quality is no longer derived from its content. */
export type CycleRole = 'NORMAL' | 'TEST' | 'RECOVERY';

export interface CycleListItem {
  id: string;
  name: string;
  displayName: string | null;
  identifier: string;
  role: CycleRole;
  enableScaling: boolean;
  minWeekNumber: number;
  maxWeekNumber: number;
  sports: string[];
  priorityByFormat: Record<string, number>;
  usedInCount: number;
  isDraft: boolean;
  updatedAt: string;
}

export interface CycleList {
  items: CycleListItem[];
  total: number;
}

export interface CycleKpis {
  total: number;
  usedInPlans: number;
  drafts: number;
}

export interface CycleWeekPriority {
  formatId: string;
  formatIdentifier: string;
  weekNumber: number;
  priority: number;
}

export interface CycleDetail extends CycleListItem {
  weekPriorities: CycleWeekPriority[];
}

export interface CyclePayload {
  name: string;
  displayName?: string | null;
  identifier?: string;
  role: CycleRole;
  enableScaling: boolean;
  minWeekNumber: number;
  maxWeekNumber: number;
  sports: string[];
  weekPriorities: { formatId: string; weekNumber: number; priority: number }[];
}

export interface CycleDeleteImpact {
  plans: { id: string; name: string }[];
}

export interface CycleSessionInput {
  /** The saved placement this one is (D-c): sent back, the backend updates it in place. */
  id?: string;
  weekNumber: number;
  dayOfWeek?: number | null;
  workoutId: string;
  /** Rank among the sessions of the same sport in the week, per plan format, tests included (D26). */
  priorities: { formatId: string; priority: number }[];
  linkKey?: string | null;
}

export interface CycleSessionPriority {
  formatId: string;
  formatIdentifier: string;
  priority: number;
}

export interface CycleSession {
  id: string;
  weekNumber: number;
  dayOfWeek: number | null;
  workoutId: string;
  workoutName: string;
  sportIdentifier: string;
  themeLabel: string;
  durationSec: number | null;
  priorities: CycleSessionPriority[];
  isTest: boolean;
  linkId: string | null;
  linkOrder: number | null;
}

export interface CycleSessionsResponse {
  maxWeekNumber: number;
  weeks: { weekNumber: number; sessions: CycleSession[] }[];
}

export interface CycleUsage {
  plans: { id: string; name: string; orderIndex: number; weekCount: number }[];
}

export interface CycleFilters {
  search?: string;
  filter?: string;
  page?: number;
  pageSize?: number;
}

export const getCycles = async (filters: CycleFilters): Promise<CycleList> => {
  const params: Record<string, string | number> = {};
  if (filters.search) params.search = filters.search;
  if (filters.filter && filters.filter !== 'all') params.filter = filters.filter;
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;
  const response = await apiClient.get('admin/cycles', { params });
  return response.data as CycleList;
};

export const getCycleKpis = async (): Promise<CycleKpis> => {
  const response = await apiClient.get('admin/cycles/kpis');
  return response.data as CycleKpis;
};

export const getCycle = async (id: string): Promise<CycleDetail> => {
  const response = await apiClient.get(`admin/cycles/${id}`);
  return response.data as CycleDetail;
};

export const createCycle = async (payload: CyclePayload): Promise<CycleDetail> => {
  const response = await apiClient.post('admin/cycles', payload);
  return response.data as CycleDetail;
};

export const updateCycle = async (id: string, payload: CyclePayload): Promise<CycleDetail> => {
  const response = await apiClient.put(`admin/cycles/${id}`, payload);
  return response.data as CycleDetail;
};

export const deleteCycle = async (id: string): Promise<void> => {
  await apiClient.delete(`admin/cycles/${id}`);
};

export const getCycleSessions = async (id: string): Promise<CycleSessionsResponse> => {
  const response = await apiClient.get(`admin/cycles/${id}/sessions`);
  return response.data as CycleSessionsResponse;
};

export const saveCycleSessions = async (
  id: string,
  sessions: CycleSessionInput[]
): Promise<CycleSessionsResponse> => {
  const response = await apiClient.put(`admin/cycles/${id}/sessions`, { sessions });
  return response.data as CycleSessionsResponse;
};

export const getCycleUsage = async (id: string): Promise<CycleUsage> => {
  const response = await apiClient.get(`admin/cycles/${id}/usage`);
  return response.data as CycleUsage;
};
